import { Box, BoxProps, Button, Card, Layer, Spinner, Text, TextProps } from 'grommet';
import { Close, IconProps } from 'grommet-icons';
import { PropsWithChildren, ReactNode } from 'react';
import { borderRadiusSize } from 'src/theme';
import { getAnimProps } from 'src/utils/styling';
import { Divider } from './Divider';

export type ModalCardProps = PropsWithChildren & {
  onClose: () => void;
  loading?: boolean;
  title?: string;
  icon?: ReactNode;
  saveMessage?: string;
  textColor?: TextProps['color'];
  iconColor?: IconProps['color'];
  cardProps?: BoxProps;
  headerProps?: BoxProps;
  headerSubtext?: ReactNode;
  headerSubtextProps?: TextProps;
  bodyProps?: BoxProps;
  loadingProps?: BoxProps;
  noLayerComponent?: boolean;
};

export const ModalCard = ({
  onClose,
  loading = false,
  title,
  icon,
  saveMessage,
  textColor = 'accent-1',
  iconColor,
  cardProps,
  headerProps,
  headerSubtext,
  headerSubtextProps,
  bodyProps,
  loadingProps,
  noLayerComponent,
  children,
}: ModalCardProps) => {
  /** Render **/

  const renderCard = () => (
    <Card
      round={borderRadiusSize}
      background="light-1"
      width={{ min: '600px', max: '800px' }}
      height={{ max: '80vh' }}
      {...cardProps}
    >
      {/* Header */}
      <Box
        background="light-3"
        direction="row"
        align="center"
        justify="between"
        height="92px"
        gap="medium"
        style={{ zIndex: 1, minHeight: '92px' }}
        elevation="small"
        pad={{ right: 'medium' }}
        {...headerProps}
      >
        <Box direction="column" gap="xsmall">
          <Box
            margin={{ left: 'medium' }}
            animation={getAnimProps()}
            justify="start"
            align="center"
            direction="row"
            gap="small"
            pad={{ right: 'none' }}
          >
            {icon}
            <Text truncate size="xlarge">
              {title}
            </Text>
          </Box>
          <Divider height="2px" />
          {headerSubtext && (
            <Box alignSelf="end">
              <Text size="small" {...headerSubtextProps}>
                {headerSubtext}
              </Text>
            </Box>
          )}
        </Box>
        <Button icon={<Close size="medium" color={iconColor} />} onClick={onClose} />
      </Box>

      {/* Body */}
      {
        <Box overflow={{ vertical: 'scroll' }} pad="medium" {...bodyProps}>
          {children}
        </Box>
      }

      {/* Loading */}
      {loading && (
        <Box align="center" justify="center" height="small" {...loadingProps}>
          <Spinner color={iconColor} />
          <Text margin={{ top: 'small' }} color={textColor}>
            {saveMessage || 'Saving changes...'}
          </Text>
        </Box>
      )}
    </Card>
  );

  return noLayerComponent ? (
    renderCard()
  ) : (
    <Layer onEsc={onClose} onClickOutside={onClose} position="center" modal background="transparent">
      {renderCard()}
    </Layer>
  );
};
